import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { RiArrowLeftLine, RiRulerLine } from 'react-icons/ri'
import { useDocumentTypes } from '@/api/useDocumentTypes'
import { useCreateProject } from '@/api/useProjects'
import { Input } from '@/components/forms'
import { Button, Spinner } from '@/components/ui'
import { ROUTES, buildRoute } from '@/constants/routes'
import styles from './NewProject.module.css'

export function NewProject() {
    const navigate = useNavigate()
    const { data: documentTypes, isLoading: loadingTypes } = useDocumentTypes()
    const createProject = useCreateProject()

    const [name, setName] = useState('')
    const [selectedType, setSelectedType] = useState<number | null>(null)
    const [customWidth, setCustomWidth] = useState('')
    const [customHeight, setCustomHeight] = useState('')

    const docTypes = Array.isArray(documentTypes) ? documentTypes : []
    const selectedDocType = docTypes.find((dt) => dt.id === selectedType)

    const handleCreate = () => {
        if (!name.trim()) {
            toast.error('Ingresa un nombre para el proyecto')
            return
        }

        const width = selectedDocType ? selectedDocType.default_width_mm : parseInt(customWidth, 10)
        const height = selectedDocType ? selectedDocType.default_height_mm : parseInt(customHeight, 10)

        if (!width || !height || width <= 0 || height <= 0) {
            toast.error('Ingresa dimensiones válidas')
            return
        }

        createProject.mutate(
            {
                name: name.trim(),
                document_type: selectedType,
                width_mm: width,
                height_mm: height,
            },
            {
                onSuccess: (project) => {
                    toast.success('Proyecto creado')
                    navigate(buildRoute.editor(project.id))
                },
                onError: () => toast.error('Error al crear proyecto'),
            }
        )
    }

    return (
        <div className={styles.container}>
            <button className={styles.backButton} onClick={() => navigate(ROUTES.projects)}>
                <RiArrowLeftLine /> Volver a proyectos
            </button>

            <div className={styles.pageHeader}>
                <h1 className={styles.title}>Nuevo Proyecto</h1>
                <p className={styles.subtitle}>
                    Elige un nombre y el formato de tu diseño. Podrás modificarlo después en el editor.
                </p>
            </div>

            <div className={styles.formCard}>
                <div className={styles.form}>
                    <Input
                        name="name"
                        variant="bordered"
                        value={name}
                        setValue={setName}
                        label="Nombre del proyecto"
                        placeholder="Ej: Tarjeta de presentación"
                    />

                    <div className={styles.section}>
                        <h3 className={styles.sectionTitle}>Tipo de documento</h3>
                        <p className={styles.sectionDesc}>
                            Selecciona un formato predefinido o usa dimensiones personalizadas.
                        </p>
                        {loadingTypes ? (
                            <div className={styles.loadingTypes}>
                                <Spinner size="sm" />
                            </div>
                        ) : (
                            <div className={styles.typeGrid}>
                                {docTypes.map((dt) => (
                                    <button
                                        key={dt.id}
                                        className={`${styles.typeCard} ${selectedType === dt.id ? styles.selected : ''}`}
                                        onClick={() => setSelectedType(dt.id)}
                                    >
                                        <span className={styles.typeName}>{dt.name}</span>
                                        <span className={styles.typeSize}>
                                            {dt.default_width_mm} x {dt.default_height_mm} mm
                                        </span>
                                    </button>
                                ))}
                                <button
                                    className={`${styles.typeCard} ${styles.customCard} ${selectedType === null ? styles.selected : ''}`}
                                    onClick={() => setSelectedType(null)}
                                >
                                    <RiRulerLine className={styles.customIcon} />
                                    <span className={styles.typeName}>Personalizado</span>
                                </button>
                            </div>
                        )}
                    </div>

                    {selectedType === null && (
                        <div className={styles.customSize}>
                            <Input
                                name="width"
                                variant="bordered"
                                value={customWidth}
                                setValue={setCustomWidth}
                                label="Ancho (mm)"
                                placeholder="210"
                            />
                            <Input
                                name="height"
                                variant="bordered"
                                value={customHeight}
                                setValue={setCustomHeight}
                                label="Alto (mm)"
                                placeholder="297"
                            />
                        </div>
                    )}

                    <div className={styles.formFooter}>
                        <Button
                            variant="secondary"
                            onClick={() => navigate(ROUTES.projects)}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleCreate}
                            disabled={createProject.isPending}
                        >
                            {createProject.isPending ? <Spinner size="sm" variant="secondary" /> : 'Crear Proyecto'}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    )
}
